import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GroupWorkIcon from '@mui/icons-material/GroupWork'
import SettingsIcon from '@mui/icons-material/Settings'
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material'
import { useTranslation } from 'react-i18next'

import GroupsTabContentSkeleton from '@/features/bidding/components/cascading/activation-groups/GroupsTabContentSkeleton'
import BidLadderContentSkeleton from '@/features/bidding/components/cascading/bid-ladder/BidLadderContentSkeleton'

const COLLAPSED_SIDEBAR_WIDTH = 56
const EXPANDED_SIDEBAR_PERCENTAGE = 0.35
const MAX_EXPANDED_WIDTH = 500

type BidOfferCascadingSkeletonProps = {
  isCollapsed?: boolean
}

function BidOfferCascadingSkeleton({ isCollapsed = false }: Readonly<BidOfferCascadingSkeletonProps>) {
  const { t } = useTranslation()

  const tabConfig = [
    {
      label: t('bidding.bidding_cascading.groups_tab.tab_title'),
      icon: <GroupWorkIcon />,
    },
    {
      label: t('bidding.bidding_cascading.configuration_tab.tab_title'),
      icon: <SettingsIcon />,
    },
  ]

  return (
    <Box data-testid="bid-offer-cascading-skeleton" sx={{ display: 'contents' }}>
      {/* Left Column - uses percentage width with same constraints as actual sidebar */}
      <Box
        sx={{
          width: isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : `${EXPANDED_SIDEBAR_PERCENTAGE * 100}%`,
          maxWidth: isCollapsed ? COLLAPSED_SIDEBAR_WIDTH : MAX_EXPANDED_WIDTH,
          minWidth: COLLAPSED_SIDEBAR_WIDTH,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Collapsed state - icon tabs */}
        {isCollapsed && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Tooltip placement="right" title={t('bidding.bidding_cascading.expand_sidebar')}>
              <IconButton
                disabled
                aria-expanded={false}
                aria-label={t('bidding.bidding_cascading.expand_sidebar')}
                sx={{ m: 1 }}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
              {tabConfig.map((tab, index) => (
                <Tooltip key={tab.label} placement="right" title={tab.label}>
                  <IconButton
                    disabled
                    aria-label={tab.label}
                    color={index === 0 ? 'primary' : 'default'}
                    sx={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      minHeight: 40,
                      flexShrink: 0,
                      backgroundColor: index === 0 ? 'action.selected' : 'transparent',
                    }}
                  >
                    {tab.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        {/* Expanded state - horizontal tabs */}
        {!isCollapsed && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                scrollButtons="auto"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  '& .MuiTabs-indicator': {
                    transition: 'none',
                  },
                }}
                value={0}
                variant="scrollable"
              >
                <Tab label={t('bidding.bidding_cascading.groups_tab.tab_title')} />
                <Tab label={t('bidding.bidding_cascading.configuration_tab.tab_title')} />
              </Tabs>

              <Box sx={{ borderLeft: 1, borderColor: 'divider', height: 24, mx: 1 }} />

              <IconButton disabled aria-label="Collapse sidebar" sx={{ mr: 1 }}>
                <ChevronLeftIcon />
              </IconButton>
            </Box>

            {/* Left column content - show Groups tab skeleton by default */}
            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
              <GroupsTabContentSkeleton />
            </Box>
          </>
        )}
      </Box>

      {/* Right Column - fills remaining space */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <BidLadderContentSkeleton />
      </Box>
    </Box>
  )
}

export default BidOfferCascadingSkeleton
